<template>
  <MainFormLayout layout="two-column">
    <section class="mb-20">
      <div class="mb-20">
        <h1
          id="articleInformationLabel"
          class="font-bitter text-3xl font-bold mb-4">
          Title &amp; abstract
        </h1>
        <p class="mb-4">
          Enter the title and abstract of your manuscript.
        </p>
      </div>

      <h2 class="font-roboto text-2xl font-bold border-b border-royal-blue-20 pb-4 mb-8">
        Article information
      </h2>

      <div class="md:flex justify-between items-end">
        <div>
          <div id="article-title-required">
            <h3
              class="font-roboto text-xl font-bold mb-2 mt-8"
              id="articleTitleLabel">
              Article title
            </h3>
          </div>
          <p class="mb-4">
            Enter the title of your manuscript. Maximum {{ limits.manuscript.words.title }} words.
          </p>
        </div>

        <IngestedBadge
          v-if="hasExtractedTitle"
          id="ingested-badge-title"
          class="mb-4" />
      </div>

      <TipTapEditor
        :dataTestId="['test-tipTap-article-title', 'article-title']"
        v-model="title"
        :wordLimit="limits.manuscript.words.title"
        :disabledOptions="['Text format hyperlink']"
        :id="'titleEditor'"
        :placeHolder="'Enter your title...'"
        name="title"
        ref="titleEditor"
        @current-word-number="handleFirstEditorWordNumber"
        :wordCountId="'titleEditorWordCounter'"
        counterType="words">
      </TipTapEditor>

      <div
        v-if="firstEditorWordNumber >= 51"
        data-testid="title-max-words-warning"
        id="titleMaxWordsError"
        class="items-top mt-4 flex space-x-2">
        <InlineWarningMessage
          :errorMessage="`Your title must not exceed ${limits.manuscript.words.title} words`"
          data-testid="input-error-message"
          errorTestId="title-max-words"
        />
      </div>

      <div
        v-if="canDisplayErrors && !firstEditorWordNumber"
        id="titleNoContentError"
        class="items-top mt-4 flex space-x-2">
        <InlineWarningMessage
          errorMessage="You must enter an article title"
          data-testid="input-error-message"
          errorTestId="article-title"
        />
      </div>

      <div class="md:flex justify-between items-end">
        <div>
          <div id="article-abstract-required">
            <h3
              class="font-roboto text-xl font-bold mb-2 mt-8"
              id="articleAbstractLabel">
              Abstract
            </h3>
          </div>

          <p class="mb-4">
            Enter the abstract of your manuscript. Maximum {{ limits.manuscript.words.abstract }} words.
          </p>
        </div>

        <IngestedBadge
          v-if="hasExtractedAbstract"
          id="ingested-badge-abstract"
          class="mb-4 whitespace-nowrap" />
      </div>

      <TipTapEditor
        :dataTestId="['test-tipTap-article-abstract', 'article-abstract']"
        v-model="abstract"
        :wordLimit="limits.manuscript.words.abstract"
        :disabledOptions="['Text format hyperlink']"
        :id="'abstractEditor'"
        :placeHolder="'Enter your abstract...'"
        name="abstract"
        ref="abstractEditor"
        @current-word-number="handleSecondEditorWordNumber"
        :wordCountId="'abstractEditorWordCounter'"
        counterType="words">
      </TipTapEditor>

      <div
        v-if="secondEditorWordNumber >= 351"
        data-testid="abstract-max-words-warning"
        id="abstractMaxWordsError"
        class="items-top mt-4 flex space-x-2">
        <InlineWarningMessage
          :errorMessage="`Your abstract must not exceed ${limits.manuscript.words.abstract} words`"
          data-testid="input-error-message"
          errorTestId="abstract-max-words"
        />
      </div>

      <div
        v-if="canDisplayErrors && !secondEditorWordNumber"
        id="abstractNoContentError"
        class="items-top mt-4 flex space-x-2">
        <InlineWarningMessage
          errorMessage="You must enter an abstract"
          data-testid="input-error-message"
          errorTestId="article-abstract"
        />
      </div>
    </section>
    <section class="mb-20">
      <div
        v-if="crystalDataProvided"
        id="ccdcd-required">
        <h2
          class="font-roboto text-2xl font-bold border-b border-royal-blue-20 pb-4 mb-8"
          id="ccdcDepositionNumbersTitle">
          Crystal data
        </h2>
        <div>
          <Field
            name="reason"
            v-slot="{ field, errors }"
            :rules="{ 'max-characters-length': [limits.CCDC.characters.data, 'Your CCDC numbers must not exceed'] }"
            :value="ccdcNumbers"
          >
            <FormTextArea
              id="ccdcDepositionNumbers"
              label="Small molecule single crystal data"
              v-model="ccdcNumbers"
              v-bind="field"
              :errorMessage="errors[0]"
              :rows="6"
              class="data-hj-suppress"
              help-text="If your manuscript reports new small molecule single crystal data, give your CCDC deposition numbers here, separated by a space."
              data-testid="test-ccdc-text-area"
            />
            <div
              v-if="canDisplayErrors && !ccdcNumbers"
              id="ccdcNoContentError"
              class="items-top mt-4 flex space-x-2">
              <InlineWarningMessage
                errorMessage="You must enter at least one CCDC deposition number"
                data-testid="input-error-message"
                errorTestId="ccdc-deposition-number"
              />
            </div>
          </Field>
          <TextCounter
            :text="ccdcNumbers"
            mode="characters"
            :max-count="limits.CCDC.characters.data"
            class="mb-8"
          />
        </div>
        <p class="mt-4">
          Note that you must deposit your crystal structures and obtain CCDC deposition numbers before submission.
        </p>
        <p>
          You must also ensure that any revised crystal structures are deposited with the CCDC before submitting your
          revised manuscript.
        </p>
        <span>See </span>
        <a
          class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited"
          target="_blank"
          href="https://www.rsc.org/publishing/publish-with-us/publish-a-journal-article/experimental-reporting"
          id="lnk-articleInfo-crystalData"
        >Experimental reporting guideline</a>
        <span> for more information.</span>
      </div>
    </section>

    <PageValidationErrors
      v-if="showErrors"
      :failed-rules="failedRules"
      variant="error">
    </PageValidationErrors>

    <div id="pnlPageButtons">
      <ButtonComponent
        :label="store.state.finalReviewVisited ? 'Update and review' : 'Next'"
        variant="primary"
        @customClick="next"
        data-testid="go-to-author-information-page"
        :customClick="next"
        id="articleInfoNextButton" />
    </div>
    <DialogComponent
      v-if="store.state.hasExtractedDataFromFile"
      :modelValue="store.state.modalShownSinceExtraction"
      class="lg:w-auto">
      <ExtractionConfirmationModal @close="closeModal">
      </ExtractionConfirmationModal>
    </DialogComponent>
  </MainFormLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import PageValidationErrors from '../components/common/PageValidationErrors.vue'
import { SubmissionFileType, SubmissionFile, VerifyResult } from '@/types'
import MainFormLayout from '../layouts/MainFormLayout.vue'
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'
import { useStore } from 'vuex'
import validateArticleInfoView from '../services/validation/articleInfo'
import TipTapEditor from '../components/common/TipTapEditor.vue'
import ButtonComponent from '../components/ui-components/ButtonComponent.vue'
import FormTextArea from '../components/ui-components/FormTextArea.vue'
import { trackEvent } from '../services/gtm'
import DialogComponent from '../components/ui-components/DialogComponent.vue'
import ExtractionConfirmationModal from '../components/article/ExtractionConfirmationModal.vue'
import { Field, useForm } from 'vee-validate'
import InlineWarningMessage from '@/components/common/InlineWarningMessage.vue'
import TextCounter from '@/components/common/TextCounter.vue'
import IngestedBadge from '@/components/common/IngestedBadge.vue'
import limits from '../constants/limits'

const router = useRouter()
const route = useRoute()
const store = useStore()

const firstEditorWordNumber = ref(0)
const secondEditorWordNumber = ref(0)
const showExtractionModal = ref(false)
const { validateField } = useForm()

const title = ref(store.state.activeSubmission.title ?? '')
const abstract = ref(store.state.activeSubmission.abstract ?? '')
const ccdcNumbers = ref(store.state.activeSubmission.ccdcNumbers || '')

const canDisplayErrors = ref(false)

onMounted(() => {
  store.commit('_setStep', route.meta.step)
  validateField('reason')
  if (hasExtractedData.value && !store.getters.hasIngestedData) {
    showExtractionModal.value = true
  }
})

const next = async () => {
  await validateArticleInfoView(
    store,
    computed(() => title.value),
    computed(() => abstract.value),
    computed(() => ccdcNumbers.value),
  )
  trackEvent({
    event: 'ArticleInfoValidationWarnings',
    category: 'Article Info Validation Warnings',
    action: 'click',
    articleInfoValidationWarnings: verifyResult.value?.errors ?? [],
  })
  canDisplayErrors.value = true
  if (canProgress.value) {
    router.push({ name: nextDestination.value })
  }
}

const nextDestination = computed((): string => {
  return store.state.finalReviewVisited ? 'final-review' : 'author-info'
})

const closeModal = () => {
  store.commit('setModalShownSinceExtraction', false)
}

const handleFirstEditorWordNumber = (wordNumber: number): void => {
  firstEditorWordNumber.value = wordNumber
}

const handleSecondEditorWordNumber = (wordNumber: number): void => {
  secondEditorWordNumber.value = wordNumber
}

const verifyResult = computed<VerifyResult | null>(() => {
  return store.getters.getVerifyResultByName('articleInfoVerifyResult') as VerifyResult | null
})

const failedRules = computed(() => verifyResult.value?.failedRules ?? {})

const canProgress = computed(() =>
  !wordLimitIsExceeded.value
  && !ccdcErrorMessage.value
  && (verifyResult.value?.valid ?? false),
)

const showErrors = computed((): boolean => {
  return (verifyResult.value?.errors?.length ?? 0) !== 0 && canDisplayErrors.value
})

const crystalDataProvided = computed((): boolean => {
  return store.state.activeSubmission.files.some((file: SubmissionFile) => file.type === SubmissionFileType.CrystalData)
})

const wordLimitIsExceeded = computed((): boolean => {
  return (
    firstEditorWordNumber.value >= 51 || secondEditorWordNumber.value >= 351
  )
})

const ccdcErrorMessage = computed(() => ccdcNumbers.value.length > limits.CCDC.characters.data)

let debounceTimeouts: Record<string, ReturnType<typeof setTimeout> | null> = {
  title: null,
  abstract: null,
}

const hasExtractedTitle = computed(() => {
  const snap = store.state.extractedSnapshot || {}
  return !!snap.title
})

const hasExtractedAbstract = computed(() => {
  const snap = store.state.extractedSnapshot || {}
  return !!snap.abstract
})
const hasExtractedData = computed(() =>
  store.state.hasExtractedDataFromFile,
)

function debouncedValidate(field: 'title' | 'abstract' | 'ccdcNumbers') {
  if (debounceTimeouts[field]) clearTimeout(debounceTimeouts[field]!)

  debounceTimeouts[field] = setTimeout(async () => {
    await validateArticleInfoView(
      store,
      computed(() => title.value),
      computed(() => abstract.value),
      computed(() => ccdcNumbers.value),
    )
  }, 300)
}

watch(title, (val) => {
  const snap = (store.state as any).extractedSnapshot || {}
  if (snap.title) store.commit('_invalidateExtractedSnapshotField', 'title')
  store.commit('_setTitle', val)
  debouncedValidate('title')
})

watch(abstract, (val) => {
  const snap = (store.state as any).extractedSnapshot || {}
  if (snap.abstract) store.commit('_invalidateExtractedSnapshotField', 'abstract')
  store.commit('_setAbstract', val)
  debouncedValidate('abstract')
})

watch(ccdcNumbers, (val) => {
  store.state.activeSubmission.ccdcNumbers = val
  store.commit('_setCcdcNumbers', val)
  debouncedValidate('ccdcNumbers')
})

onBeforeRouteLeave(async () => {
  store.commit('setModalShownSinceExtraction', false)
  await validateArticleInfoView(
    store,
    computed(() => title.value),
    computed(() => abstract.value),
    computed(() => ccdcNumbers.value),
  )
})
</script>
